import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { monthBoundsYyyyMm } from "@/modules/reports/domain/lib/month-bounds";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { DownloadIcon, ListIcon, PrinterIcon, RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InstructorSessionByInstructorItemDTO } from "../api/client";
import { reportsApi } from "../api/client";
import {
  useCreateInstructorPayrollBatchMutation,
  useInstructorMonthlyPayoutLinesQuery,
  useInstructorMonthlyPayoutSummaryQuery,
  useInstructorPayrollBatchByMonthQuery,
  useInstructorPayrollBatchDetailQuery,
  useMarkInstructorPayrollBatchPaidMutation,
} from "../api/queries";
import {
  aggregateMonthlyPayoutTotals,
  countPayoutTableFilter,
  resolvePayoutBatchWorkflowStep,
  rowMatchesPayoutTableFilter,
  type PayoutTableFilter,
} from "../lib/instructor-payout-display";
import {
  computeBatchLiveDelta,
  exportInstructorPayoutLinesCsv,
  exportInstructorPayoutSummaryCsv,
} from "../lib/instructor-payout-export";
import { InstructorMonthlyPayoutCompactTable } from "../ui/InstructorMonthlyPayoutCompactTable";
import { InstructorMonthlyPayoutDetailDrawer } from "../ui/InstructorMonthlyPayoutDetailDrawer";
import { InstructorMonthlyPayoutPayrollBar } from "../ui/InstructorMonthlyPayoutPayrollBar";
import { InstructorMonthlyPayoutSummaryCards } from "../ui/InstructorMonthlyPayoutSummaryCards";
import { InstructorMonthlyPayoutTableFilters } from "../ui/InstructorMonthlyPayoutTableFilters";

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

const EMPTY_INSTRUCTOR_PAYOUT_ROWS: InstructorSessionByInstructorItemDTO[] = [];

type SortKey = "name" | "payout_desc" | "payout_asc";

export function InstructorMonthlyPayoutReportPage() {
  const { has } = usePermissions();
  const canConfirmPayroll = has("payroll:confirm");
  const [draftYear, setDraftYear] = useState(() => new Date().getFullYear());
  const [draftMonth, setDraftMonth] = useState(() => new Date().getMonth() + 1);
  const [run, setRun] = useState<{ year: number; month: number }>(() => ({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  }));
  const [searchQuery, setSearchQuery] = useState("");
  const [tableFilter, setTableFilter] = useState<PayoutTableFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [detail, setDetail] = useState<{
    instructorId: string;
    instructorName: string;
  } | null>(null);
  const [payrollSelectedIds, setPayrollSelectedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [linesExporting, setLinesExporting] = useState(false);
  const lastPayrollRunRef = useRef<{ y: number; m: number } | null>(null);

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 8 }, (_, i) => y - 5 + i);
  }, []);

  const summaryQuery = run;
  const { data: summaryRows, isLoading: summaryLoading, refetch } =
    useInstructorMonthlyPayoutSummaryQuery(summaryQuery);
  const rows = summaryRows ?? EMPTY_INSTRUCTOR_PAYOUT_ROWS;

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = rows.filter((r) => rowMatchesPayoutTableFilter(r, tableFilter));
    if (q) {
      list = list.filter((r) => r.instructorName.toLowerCase().includes(q));
    }
    list = [...list];
    if (sortKey === "name") {
      list.sort((a, b) =>
        a.instructorName.localeCompare(b.instructorName, "lo"),
      );
    } else if (sortKey === "payout_desc") {
      list.sort((a, b) => b.estimatedPayout - a.estimatedPayout);
    } else {
      list.sort((a, b) => a.estimatedPayout - b.estimatedPayout);
    }
    return list;
  }, [rows, searchQuery, sortKey, tableFilter]);

  const filterCounts = useMemo(
    () => ({
      general_class: countPayoutTableFilter(rows, "general_class"),
      private_lesson: countPayoutTableFilter(rows, "private_lesson"),
      partner: countPayoutTableFilter(rows, "partner"),
      room_rental_assist: countPayoutTableFilter(rows, "room_rental_assist"),
      general_class_assist: countPayoutTableFilter(rows, "general_class_assist"),
      rate_issue: countPayoutTableFilter(rows, "rate_issue"),
    }),
    [rows],
  );

  const tableEmptyMessage = useMemo(() => {
    if (rows.length === 0) {
      return "ບໍ່ມີ ຊົ່ວໂມງຮຽນສຳເລັດ + ການເຊັກຊື່ຄູໃນຊ່ວງເດືອນນີ້";
    }
    if (tableFilter !== "all" || searchQuery.trim()) {
      return "ບໍ່ພົບຄູຕາມເງື່ອນໄຂກັ່ນຕົວ / ຄົ້ນຫາ";
    }
    return "ບໍ່ພົບຂໍ້ມູນໃນຊ່ວງວັນທີ່ເລືອກ";
  }, [rows.length, tableFilter, searchQuery]);

  const { data: savedBatch, isLoading: batchLoading } =
    useInstructorPayrollBatchByMonthQuery(
      run?.year ?? null,
      run?.month ?? null,
    );

  const batchForRun =
    run && savedBatch?.year === run.year && savedBatch.month === run.month
      ? savedBatch
      : null;

  const workflowStep = resolvePayoutBatchWorkflowStep(batchForRun);

  const { data: batchDetail } = useInstructorPayrollBatchDetailQuery(
    batchForRun?.id ?? null,
  );

  const batchLiveDelta = useMemo(() => {
    if (!batchForRun || !batchDetail?.lines.length) return null;
    return computeBatchLiveDelta(
      batchDetail.lines.map((l) => l.instructorId),
      batchForRun.totalAmountKip,
      rows,
    );
  }, [batchForRun, batchDetail, rows]);

  const createBatchMutation = useCreateInstructorPayrollBatchMutation();
  const markPaidMutation = useMarkInstructorPayrollBatchPaidMutation();

  const linesQueryKey =
    detail && run
      ? {
        year: run.year,
        month: run.month,
        instructorId: detail.instructorId,
      }
      : null;
  const { data: lineRows = [], isLoading: linesLoading } =
    useInstructorMonthlyPayoutLinesQuery(linesQueryKey);

  const detailSummaryRow = useMemo(
    () =>
      detail
        ? (rows.find((r) => r.instructorId === detail.instructorId) ?? null)
        : null,
    [detail, rows],
  );

  const totals = useMemo(
    () => aggregateMonthlyPayoutTotals(rows),
    [rows],
  );

  const payrollSelectionTotals = useMemo(() => {
    let sum = 0;
    let n = 0;
    for (const r of rows) {
      if (payrollSelectedIds.has(r.instructorId)) {
        sum += r.estimatedPayout;
        n += 1;
      }
    }
    return { sum, n };
  }, [rows, payrollSelectedIds]);

  useEffect(() => {
    setRun({ year: draftYear, month: draftMonth });
    setTableFilter("all");
    setSearchQuery("");
  }, [draftYear, draftMonth]);

  useEffect(() => {
    if (!run) {
      lastPayrollRunRef.current = null;
      setPayrollSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    if (rows.length === 0) return;
    const prev = lastPayrollRunRef.current;
    if (prev?.y === run.year && prev?.m === run.month) return;
    lastPayrollRunRef.current = { y: run.year, m: run.month };
    const ids = rows.map((r) => r.instructorId);
    setPayrollSelectedIds((prev) => {
      if (prev.size === ids.length && ids.every((id) => prev.has(id))) {
        return prev;
      }
      return new Set(ids);
    });
  }, [run, rows]);

  const togglePayrollInstructor = useCallback(
    (instructorId: string, checked: boolean) => {
      setPayrollSelectedIds((prev) => {
        const next = new Set(prev);
        if (checked) next.add(instructorId);
        else next.delete(instructorId);
        return next;
      });
    },
    [],
  );

  const selectAllPayrollInstructors = useCallback(
    (select: boolean) => {
      if (select) {
        setPayrollSelectedIds(new Set(rows.map((r) => r.instructorId)));
      } else {
        setPayrollSelectedIds(new Set());
      }
    },
    [rows],
  );

  const openDetail = useCallback((row: InstructorSessionByInstructorItemDTO) => {
    setDetail({
      instructorId: row.instructorId,
      instructorName: row.instructorName,
    });
  }, []);

  const onRefresh = useCallback(() => {
    setRun({ year: draftYear, month: draftMonth });
    void refetch();
  }, [draftYear, draftMonth, refetch]);

  const exportSummaryCsv = useCallback(() => {
    if (!run) return;
    const { dateFrom, dateTo } = monthBoundsYyyyMm(run.year, run.month);
    exportInstructorPayoutSummaryCsv(run, dateFrom, dateTo, rows);
  }, [run, rows]);

  const printSummary = useCallback(() => {
    if (!run) return;
    const url = reportsApi.instructorMonthlyPayout.printUrl({
      year: run.year,
      month: run.month,
    });
    window.open(url, "_blank", "noopener,noreferrer");
  }, [run]);

  const exportLinesCsv = useCallback(async () => {
    if (!run || rows.length === 0) return;
    const { dateFrom, dateTo } = monthBoundsYyyyMm(run.year, run.month);
    setLinesExporting(true);
    try {
      const chunks = await Promise.all(
        rows.map(async (r) => {
          const lines = await reportsApi.instructorMonthlyPayout.getLines({
            year: run.year,
            month: run.month,
            instructorId: r.instructorId,
          });
          return lines.map((line) => ({
            ...line,
            instructorName: r.instructorName,
          }));
        }),
      );
      exportInstructorPayoutLinesCsv(run, dateFrom, dateTo, chunks.flat());
    } catch {
      toast.error("ສົ່ງອອກ CSV ລາຍລະອຽດບໍ່ສຳເລັດ");
    } finally {
      setLinesExporting(false);
    }
  }, [run, rows]);

  const handleConfirmPayroll = useCallback(async () => {
    if (!run) return;
    const { dateFrom, dateTo } = monthBoundsYyyyMm(run.year, run.month);
    const ok = await confirm({
      title: "ຢືນຢັນບັນທຶກຮອບຄ່າຈ້າງ",
      description: `ບັນທຶກ ${payrollSelectionTotals.n} ຄູ · ລວມ ${payrollSelectionTotals.sum.toLocaleString("lo-LA")} ₭ · ${run.month}/${run.year} (${dateFrom} → ${dateTo})`,
      actionText: "ຢືນຢັນບັນທຶກ",
      ActionProps: { variant: "default" },
    });
    if (!ok) return;
    createBatchMutation.mutate({
      year: run.year,
      month: run.month,
      instructorIds: Array.from(payrollSelectedIds),
    });
  }, [
    run,
    payrollSelectionTotals.n,
    payrollSelectionTotals.sum,
    payrollSelectedIds,
    createBatchMutation,
  ]);

  const handleMarkPaid = useCallback(async () => {
    if (!run || !batchForRun) return;
    const ok = await confirm({
      title: "ໝາຍວ່າຈ່າຍແລ້ວ",
      description: `ຮອບ ${run.month}/${run.year} · ${batchForRun.totalAmountKip.toLocaleString("lo-LA")} ₭ · ${batchForRun.instructorCount} ຄູ`,
      actionText: "ໝາຍວ່າຈ່າຍແລ້ວ",
    });
    if (!ok) return;
    markPaidMutation.mutate({
      batchId: batchForRun.id,
      year: run.year,
      month: run.month,
    });
  }, [run, batchForRun, markPaidMutation]);

  const tableFooter = useMemo(
    () =>
      "ນັບຈາກການເຊັກຊື່ຄູຂອງຊົ່ວໂມງຮຽນ / ຮຽນສ່ວນຕົວທີ່ສະຖານະສຳເລັດໃນຊ່ວງວັນທີ່ຂອງເດືອນ. ບໍ່ລວມຄັ້ງທີ່ສຳເລັດແຕ່ບໍ່ມີການເຊັກຊື່ຄູ. ຍອດຖານຕາຕະລາງເປັນການຄິດແບບລວມຕໍ່ kind (ອາດຕ່າງຈາກຜົນລວມຂອງບັນທັດທີປັດທີລະຄັ້ງ).",
    [],
  );

  const showPayrollBar =
    canConfirmPayroll && run != null && rows.length > 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ສະຫຼຸບຄ່າຈ້າງຄູສອນຕາມເດືອນ" },
          ]}
        />
      </Header>

      <Main className={showPayrollBar ? "pb-28" : undefined}>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ສະຫຼຸບຄ່າຈ້າງຄູສອນຕາມເດືອນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ເລືອກເດືອນ — ຂໍ້ມູນໂຫຼດອັດຕະໂນມັດ · ຄລິກຊື່ຄູເພື່ອເບິ່ງລາຍລະອຽດຕໍ່ຄັ້ງ
            </p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader >
            <CardTitle className="text-base">ຊ່ວງເດືອນ</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="monthly-payout-year">ປີ</Label>
              <Select
                value={String(draftYear)}
                onValueChange={(v) => setDraftYear(Number.parseInt(v, 10))}
              >
                <SelectTrigger id="monthly-payout-year" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthly-payout-month">ເດືອນ</Label>
              <Select
                value={String(draftMonth)}
                onValueChange={(v) => setDraftMonth(Number.parseInt(v, 10))}
              >
                <SelectTrigger id="monthly-payout-month" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_OPTIONS.map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="button" variant="outline" onClick={onRefresh}>
              <RefreshCwIcon className="mr-2 size-4" />
              ໂຫຼດໃໝ່
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!run || rows.length === 0}
              onClick={exportSummaryCsv}
            >
              <DownloadIcon className="mr-2 size-4" />
              CSV ສະຫຼຸບ
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!run || rows.length === 0 || linesExporting}
              onClick={() => void exportLinesCsv()}
            >
              <DownloadIcon className="mr-2 size-4" />
              {linesExporting ? "ກຳລັງສົ່ງອອກ..." : "CSV ລາຍລະອຽດ"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!run || rows.length === 0}
              onClick={printSummary}
            >
              <PrinterIcon className="mr-2 size-4" />
              PDF / ພິມ
            </Button>
          </CardContent>
        </Card>

        <InstructorMonthlyPayoutSummaryCards
          isLoading={summaryLoading}
          run={run}
          totals={totals}
          savedBatch={batchForRun}
          workflowStep={workflowStep}
          batchLiveDelta={batchLiveDelta}
          activeTableFilter={tableFilter}
          onTableFilterChange={setTableFilter}
        />

        {filterCounts.rate_issue > 0 ? (
          <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 text-sm dark:text-amber-200">
            ພົບ {filterCounts.rate_issue.toLocaleString("lo-LA")} ຄູທີ່ມີຊົ່ວໂມງຮຽນ
            ແຕ່ບໍ່ມີອັດຕາຄ່າຈ້າງຄົບ — ກົດ「ບໍ່ມີອັດຕາ」ເພື່ອກວດ
          </div>
        ) : null}

        {run && rows.length > 0 ? (
          <>
            <InstructorMonthlyPayoutTableFilters
              activeFilter={tableFilter}
              onFilterChange={setTableFilter}
              counts={filterCounts}
            />
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <div className="max-w-sm flex-1">
                <Label htmlFor="payout-instructor-search" className="sr-only">
                  ຄົ້ນຫາຄູສອນ
                </Label>
                <Input
                  id="payout-instructor-search"
                  placeholder="ຄົ້ນຫາຊື່ຄູສອນ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payout-sort" className="sr-only">
                  ຈັດລຽງ
                </Label>
                <Select
                  value={sortKey}
                  onValueChange={(v) => setSortKey(v as SortKey)}
                >
                  <SelectTrigger id="payout-sort" className="w-[180px]">
                    <SelectValue placeholder="ຈັດລຽງ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">ຊື່ (A–Z)</SelectItem>
                    <SelectItem value="payout_desc">ຄ່າຈ້າງ (ສູງ → ຕ່ຳ)</SelectItem>
                    <SelectItem value="payout_asc">ຄ່າຈ້າງ (ຕ່ຳ → ສູງ)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        ) : null}

        {!summaryLoading && run && rows.length === 0 ? (
          <div className="mb-6 rounded-xl border border-dashed bg-muted/20 px-4 py-10 text-center">
            <p className="font-medium text-sm">ບໍ່ມີຂໍ້ມູນຄ່າຈ້າງໃນເດືອນນີ້</p>
            <p className="mt-1 text-muted-foreground text-sm">
              ຕ້ອງມີ ຊົ່ວໂມງຮຽນ / ການຈອງສຳເລັດ ພ້ອມການເຊັກຊື່ຄູ
            </p>
          </div>
        ) : null}

        <InstructorMonthlyPayoutCompactTable
          isLoading={summaryLoading}
          data={filteredRows}
          emptyMessage={tableEmptyMessage}
          footerNote={run ? tableFooter : undefined}
          onOpenDetail={openDetail}
          payrollSelection={
            canConfirmPayroll && run && !batchForRun
              ? {
                selectedIds: payrollSelectedIds,
                onToggle: togglePayrollInstructor,
                onSelectAll: selectAllPayrollInstructors,
              }
              : null
          }
          renderRowActions={(row) => (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="gap-1"
              onClick={() => openDetail(row)}
            >
              <ListIcon className="size-3.5" />
              ເບິ່ງ
            </Button>
          )}
        />

        {!canConfirmPayroll ? (
          <p className="mt-4 text-muted-foreground text-xs">
            ການຢືນຢັນບັນທຶກຮອບ / ໝາຍຈ່າຍຕ້ອງມີສິດ payroll:confirm
          </p>
        ) : null}

        <InstructorMonthlyPayoutDetailDrawer
          open={detail != null}
          onClose={() => setDetail(null)}
          instructorId={detail?.instructorId ?? null}
          instructorName={detail?.instructorName ?? ""}
          run={run}
          summaryRow={detailSummaryRow}
          lines={lineRows}
          isLoading={linesLoading}
          canOverride={canConfirmPayroll && workflowStep === "review"}
        />
      </Main>

      {showPayrollBar ? (
        <InstructorMonthlyPayoutPayrollBar
          workflowStep={workflowStep}
          selectionTotals={payrollSelectionTotals}
          savedBatch={batchForRun}
          canEditSelection={!batchForRun}
          onSelectAll={selectAllPayrollInstructors}
          onConfirm={() => void handleConfirmPayroll()}
          onMarkPaid={() => void handleMarkPaid()}
          isConfirmPending={createBatchMutation.isPending}
          isMarkPaidPending={markPaidMutation.isPending}
          isLoading={summaryLoading || batchLoading}
        />
      ) : null}
    </>
  );
}
